<template>
  <div class="native-video-container">
    <video
      :id="playerId"
      ref="videoRef"
      :controls="config?.controls !== false"
      :autoplay="config?.autoplay || false"
      :muted="config?.autoplayMuted || config?.muted || false"
      :loop="config?.loop || false"
      :preload="config?.preload || 'auto'"
      :width="config?.width || '100%'"
      :height="config?.height || '100%'"
      :poster="config?.poster"
      class="native-video"
      @loadstart="onLoadStart"
      @loadeddata="onLoadedData"
      @canplay="onCanPlay"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnded"
      @error="onError"
    >
      <source :src="url" type="video/mp4" />
      <p class="no-support">
        您的浏览器不支持 HTML5 视频。请升级您的浏览器以观看视频。
      </p>
    </video>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  url: {
    type: String,
    required: true
  },
  playerId: {
    type: [String, Number],
    default: () => `video-${Date.now()}`
  },
  config: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits([
  'loadstart',
  'loadeddata', 
  'canplay',
  'play',
  'pause',
  'ended',
  'error'
])

const videoRef = ref(null)

// 事件处理函数
const onLoadStart = (event) => {
  emit('loadstart', event)
}

const onLoadedData = (event) => {
  emit('loadeddata', event)
}

const onCanPlay = (event) => {
  emit('canplay', event)
}

const onPlay = (event) => {
  emit('play', event)
}

const onPause = (event) => {
  emit('pause', event)
}

const onEnded = (event) => {
  emit('ended', event)
}

const onError = (event) => {
  console.error('Video error:', event)
  emit('error', event)
}

// 公开的方法
const play = () => {
  if (videoRef.value) {
    return videoRef.value.play()
  }
}

const pause = () => {
  if (videoRef.value) {
    videoRef.value.pause()
  }
}

const getCurrentTime = () => {
  return videoRef.value?.currentTime || 0
}

const setCurrentTime = (time) => {
  if (videoRef.value) {
    videoRef.value.currentTime = time
  }
}

const getDuration = () => {
  return videoRef.value?.duration || 0
}

const getVolume = () => {
  return videoRef.value?.volume || 1
}

const setVolume = (volume) => {
  if (videoRef.value) {
    videoRef.value.volume = Math.max(0, Math.min(1, volume))
  }
}

const mute = () => {
  if (videoRef.value) {
    videoRef.value.muted = true
  }
}

const unmute = () => {
  if (videoRef.value) {
    videoRef.value.muted = false
  }
}

const toggleMute = () => {
  if (videoRef.value) {
    videoRef.value.muted = !videoRef.value.muted
  }
}

const isPlaying = () => {
  return videoRef.value && !videoRef.value.paused && !videoRef.value.ended
}

const isPaused = () => {
  return videoRef.value?.paused || false
}

const isEnded = () => {
  return videoRef.value?.ended || false
}

// 监听URL变化
watch(() => props.url, (newUrl) => {
  if (videoRef.value && newUrl) {
    videoRef.value.load()
  }
})

// 暴露方法给父组件
defineExpose({
  play,
  pause,
  getCurrentTime,
  setCurrentTime,
  getDuration,
  getVolume,
  setVolume,
  mute,
  unmute,
  toggleMute,
  isPlaying,
  isPaused,
  isEnded,
  videoElement: videoRef
})

onMounted(() => {
  // 如果配置了自动播放，尝试播放
  if (props.config?.autoplay && videoRef.value) {
    videoRef.value.play().catch(error => {
      console.warn('Autoplay failed:', error)
    })
  }
})

onUnmounted(() => {
  // 清理资源
  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value.src = ''
    videoRef.value.load()
  }
})
</script>

<style scoped>
.native-video-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.native-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
}

.no-support {
  color: #fff;
  text-align: center;
  padding: 20px;
  background-color: #000;
}
</style>